/**
 * A janela da casa — o `Dialogo` (P-JAN-01 a 04).
 *
 * `<dialog>` nativo: foco preso, `Esc` fecha e leitor de tela entende, sem
 * biblioteca. Não uso `alert()` nem `confirm()` — bloqueiam a janela e o
 * técnico com o celular na mão perde o formulário atrás.
 *
 * Por que mora nesta pasta (SP-347d, decisão do Humberto na onda 347, opção A):
 * a janela «Escala» dos gráficos do CPTu é do visual, que a bancada e o Portal
 * do cliente têm byte a byte (`web/src/cptu/visual` = `portal-web/src/cptu/visual`,
 * cobrado pelo 300.90), e o Portal não tinha o `Dialogo`, que vivia no
 * `web/src/ui.tsx`. Em vez de uma segunda janela feita à mão, a peça veio para
 * a pasta comum: o `ui.tsx` da bancada só a reexporta, e as telas continuam
 * importando de `./ui`. O desenho é o de `design/componentes.css` (bloco
 * «Janela»), que as duas `estilo.css` importam.
 *
 * Só importa `react` (a regra do visual, 300.91).
 */
import type { JSX, ReactNode } from 'react';
import { useEffect, useRef } from 'react';

export function Dialogo({
  aberto, titulo, rotulo, aoFechar, children, larga = false, classe, dados,
}: {
  aberto: boolean; titulo: string;
  /** 283: a janela larga do Carbon (tabela de cinco colunas). */
  larga?: boolean;
  /** 277: o «label» do modal Carbon — a linha pequena acima do título que
   *  diz de onde a janela veio (ex.: «OS 2609347»). Opcional. */
  rotulo?: string;
  /** 347: classe a mais no `<dialog>`, para a folha da tela arrumar o corpo
   *  (ex.: `cptu-escala-janela`). O desenho da janela continua o da base. */
  classe?: string;
  /** 347: atributos `data-*` no `<dialog>` (`{ grandeza: 'qt' }` → `data-grandeza="qt"`). */
  dados?: Readonly<Record<string, string>>;
  aoFechar(): void; children: ReactNode;
}): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (aberto && !d.open) d.showModal();
    if (!aberto && d.open) d.close();
  }, [aberto]);

  /*
   * ─── O X, e por que ele não é enfeite ─────────────────────────────
   *
   * «Quando abro o botão categoria em equipamentos, não consigo fechar a
   *  janela (não tem um botão de voltar ou X), apenas apertando ESC»
   * (Testesolo, 01/09/2026).
   *
   * A janela TINHA um «Fechar» — no rodapé, depois de uma tabela de 38
   * categorias com rolagem própria. Quem abre para consultar não desce
   * até o fim, e o único caminho de volta virou uma tecla que ninguém
   * anunciou. Um diálogo modal engole o clique de fora e o botão de
   * voltar do navegador; sem saída visível, ele prende.
   *
   * Fica aqui, no `Dialogo`, e não naquela tela: toda janela desta
   * aplicação tem o mesmo problema no dia em que o conteúdo dela crescer.
   * O cabeçalho gruda para que o X continue à vista com a lista rolando.
   */
  /*
   * 277 · a janela no Carbon (Humberto, 05/10/2026: «atualize o design das
   * janelas para o design Carbon Testesolo»). A estrutura é a do modal do
   * Carbon: cabeçalho com o rótulo pequeno e o título leve, o X de 48 px
   * colado no canto (sem moldura — é o «ghost»), o corpo que rola sozinho
   * e, quando a janela termina em botões, o rodapé de ponta a ponta com o
   * secundário à esquerda e a ação à direita (regras em
   * `design/componentes.css`, bloco «Janela»). O cabeçalho continua fixo:
   * o X à vista com a lista rolando.
   */
  const atributos: Record<string, string> = {};
  for (const [k, v] of Object.entries(dados ?? {})) atributos[`data-${k}`] = v;
  return (
    <dialog ref={ref} className={`janela-carbon${larga ? ' larga' : ''}${classe ? ` ${classe}` : ''}`}
      onCancel={(e) => { e.preventDefault(); aoFechar(); }} aria-label={titulo} {...atributos}>
      <div className="cabeca-dialogo">
        <div className="titulos-dialogo">
          {rotulo && <span className="rotulo-dialogo">{rotulo}</span>}
          <h2>{titulo}</h2>
        </div>
        <button
          type="button"
          className="fechar-dialogo"
          aria-label="Fechar"
          title="Fechar"
          onClick={aoFechar}
        >
          <svg viewBox="0 0 32 32" width="20" height="20" aria-hidden="true" focusable="false">
            <path fill="currentColor"
              d="M24 9.4 22.6 8 16 14.6 9.4 8 8 9.4l6.6 6.6L8 22.6 9.4 24l6.6-6.6 6.6 6.6 1.4-1.4-6.6-6.6z" />
          </svg>
        </button>
      </div>
      <div className="corpo-dialogo">{children}</div>
    </dialog>
  );
}
